import { useState } from 'react';
import { toast } from 'sonner';

import { enrollPasskey } from '../passkeys/api/passkeyService';
import { esAltaYaRegistrada, mensajeDeAltaFallida } from '../quick-login/altaFallida';
import { nombreDeDispositivo } from '../quick-login/deviceName';
import { Fingerprint, Tick } from './LoginBits';

type Props = {
  /** La passkey quedó registrada en este dispositivo. */
  onActivada: () => void;
  /** «Ahora no»: no se vuelve a ofrecer en este navegador por un tiempo. */
  onAhoraNo: () => void;
};

/**
 * Se muestra tras un ingreso completo con contraseña (y 2FA) en un navegador
 * que todavía no tiene su propia passkey. Cada navegador, y cada PWA de Chrome,
 * guarda las suyas: sin esta oferta la entrada rápida no existiría ahí.
 */
export function ActivateQuickLoginOffer({ onActivada, onAhoraNo }: Props) {
  const [activando, setActivando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nombre = nombreDeDispositivo();

  async function activar() {
    setError(null);
    setActivando(true);
    try {
      await enrollPasskey(nombre, { attachment: 'platform' });
      toast.success('Entrada rápida activada en este dispositivo');
      onActivada();
    } catch (err) {
      if (esAltaYaRegistrada(err)) {
        // Este dispositivo ya tenía tu passkey: sirve para entrar, se activa igual.
        toast.success('Este dispositivo ya tenía tu passkey: entrada rápida activada');
        onActivada();
        return;
      }
      setError(mensajeDeAltaFallida(err));
      setActivando(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="dd-oferta-titulo"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        background: 'rgba(7, 11, 14, 0.78)',
        backdropFilter: 'blur(10px) saturate(140%)',
        WebkitBackdropFilter: 'blur(10px) saturate(140%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 22,
      }}
    >
      <div
        style={{
          width: 'min(380px, 100%)',
          padding: 28,
          background: 'rgba(10, 16, 21, 0.94)',
          border: '1px solid rgba(102, 224, 255, 0.32)',
          borderRadius: 10,
          boxShadow: `
            0 0 0 1px rgba(102, 224, 255, 0.08),
            0 32px 80px -28px rgba(0,0,0,0.95),
            0 0 100px -20px rgba(102, 224, 255, 0.22)
          `,
          fontFamily: 'Inter, system-ui, sans-serif',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <Tick pos="tl" />
        <Tick pos="tr" />
        <Tick pos="bl" />
        <Tick pos="br" />

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span
            style={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              border: '1px solid rgba(102, 224, 255, 0.55)',
              background:
                'radial-gradient(circle, rgba(102,224,255,0.22) 0%, rgba(102,224,255,0.06) 70%, transparent 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Fingerprint size={22} />
          </span>
          <h3
            id="dd-oferta-titulo"
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: 18,
              fontWeight: 600,
              color: '#f6fbff',
              margin: 0,
              letterSpacing: '-0.01em',
            }}
          >
            Entrá más rápido la próxima vez
          </h3>
        </div>

        <p
          style={{
            fontSize: 12.5,
            color: 'rgba(255,255,255,0.62)',
            margin: 0,
            lineHeight: 1.55,
          }}
        >
          Activá la entrada rápida en este dispositivo ({nombre}) y entrás con tu
          huella o Face ID, sin escribir email ni contraseña. La contraseña sigue
          funcionando y podés quitarla de este dispositivo cuando quieras desde
          Seguridad.
        </p>

        {error && (
          <div
            role="alert"
            style={{
              padding: '10px 12px',
              border: '1px solid rgba(255, 142, 142, 0.35)',
              background: 'rgba(255, 142, 142, 0.07)',
              borderRadius: 5,
              fontSize: 12,
              color: '#ffb4b4',
              lineHeight: 1.5,
            }}
          >
            {error}
          </div>
        )}

        <button
          type="button"
          className="dd-enter-btn"
          onClick={() => void activar()}
          disabled={activando}
          aria-busy={activando}
          autoFocus
          style={{ marginTop: 0 }}
        >
          {activando ? (
            <>
              <span className="dd-spinner" aria-hidden="true" />
              <span>Esperando biometría…</span>
            </>
          ) : (
            <span>{error ? 'Reintentar' : 'Activar en este dispositivo'}</span>
          )}
        </button>

        <button
          type="button"
          className="dd-link-btn"
          onClick={onAhoraNo}
          disabled={activando}
          style={{ alignSelf: 'center' }}
        >
          {error ? 'Seguir sin activar' : 'Ahora no'}
        </button>
      </div>
    </div>
  );
}
